import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as api from '../lib/api';
import { slugify, isUuid } from '../lib/spaceSlug';
import SpacePage from './SpacePage';

/**
 * /spaces/know-yourself (readable) and /spaces/<id> (older links) both land here.
 * Finds the Space, then shows it. Older id links are quietly upgraded to the readable address.
 */
const SpaceRoute = ({ param, onBack }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [groups, setGroups] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.getGroups()
      .then((res) => { if (!cancelled) setGroups(res.data || []); })
      .catch(() => { if (!cancelled) setGroups([]); });
    return () => { cancelled = true; };
  }, []);

  const wanted = String(param || '');
  const group = useMemo(() => {
    if (!groups) return null;
    return isUuid(wanted)
      ? groups.find((g) => g.id === wanted)
      : groups.find((g) => slugify(g.name) === wanted.toLowerCase());
  }, [groups, wanted]);

  const id = group?.id || (isUuid(wanted) ? wanted : null);

  useEffect(() => {
    if (!group) return;
    const path = `/spaces/${slugify(group.name)}`;
    if (pathname !== path) navigate(path, { replace: true });
  }, [group, pathname, navigate]);

  if (id) return <SpacePage spaceId={id} onBack={onBack} />;

  if (groups && !isUuid(wanted)) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="space-not-found">
        <h1 className="font-heading text-2xl font-semibold text-text-main mb-2">We could not find that Space</h1>
        <p className="text-text-muted mb-6">The address may have changed. You can find every Space in the list on the left of the feed.</p>
        <button onClick={onBack} className="min-h-[44px] px-6 rounded-full bg-primary text-white font-bold">Back to the feed</button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-20" aria-busy="true" role="status" aria-label="Opening Space">
      <div className="h-40 rounded-[2rem] bg-primary/10 animate-pulse" />
    </div>
  );
};

export default SpaceRoute;
